import React, { useEffect } from 'react';
import { InstagramIcon, PinterestIcon, TiktokIcon, YoutubeIcon } from '@revfluence/fresh-icons/brands/esm';
import { ArrowPointerIcon, GlobeIcon, CircleInfoIcon, PaintbrushIcon, SeedlingIcon } from '@revfluence/fresh-icons/solid/esm';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@frontend/shadcn/components/ui/accordion';
import { Input } from '@frontend/shadcn/components/ui/input';
import { Label } from '@frontend/shadcn/components/ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@frontend/shadcn/components/ui/select';
import { ImagePreview } from '@frontend/app/refresh-components/ImagePreview';
import { DatePickerWithRange } from '@frontend/app/refresh-components/DateRangePicker';
import { useAuth } from '@frontend/context/authContext';
import { useGetClientLogoQuery } from '@frontend/app/hooks/useGetClientLogoQuery';
import { Separator } from '@frontend/shadcn/components/ui/separator';
import { ColorPicker } from '@frontend/applications/AffiliatesApp/pages/LandingPage/utils/shadColorPicker';
import { FontPicker } from '@frontend/app/refresh-components/FontPicker';
import { useFullPreviewUrl, useRecruitment } from './RecruitmentContext';

export const ThemingPage = () => {
  const fontSizeOptions = [
    { value: '12', label: '12' },
    { value: '14', label: '14' },
    { value: '16', label: '16' },
    { value: '20', label: '20' },
    { value: '28', label: '28' },
    { value: '32', label: '32' },
    { value: '36', label: '36' },
    { value: '48', label: '48' },
    { value: '60', label: '60' },
  ];

  const { settings, updateSettings } = useRecruitment();

  const { clientInfo } = useAuth();

  const { logoUrl } = useGetClientLogoQuery();

  useEffect(() => {
    if (clientInfo?.name) {
      updateSettings({ brandName: clientInfo.name });
    }
  }, [clientInfo?.name, updateSettings]);

  useEffect(() => {
    if (logoUrl) {
      updateSettings({ logo: logoUrl });
    }
  }, [logoUrl, updateSettings]);

  const fullUrl = useFullPreviewUrl();

  return (
    <>
      <Accordion type="multiple" className="[&_h3]:mb-0" defaultValue={['basics', 'theme', 'buttons']}>
        <AccordionItem value="basics" className="border-b-0">
          <AccordionTrigger>
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-teal-6 rounded-lg text-primary-foreground flex items-center justify-center">
                <SeedlingIcon />
              </div>
              <span className="text-foreground text-base font-medium">Basics</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="applicationTitle" className="text-xs font-medium text-foreground">
                Application Title <span className="text-xs font-normal text-primaryMuted">(Required)</span>
              </Label>
              <div className="px-[1px]">
                <Input id="applicationTitle" placeholder="[Title of Application]" value={settings.applicationTitle} onChange={(e) => updateSettings({ applicationTitle: e.target.value })} />
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-1">
                <Label htmlFor="logo" className="text-xs font-medium text-foreground">
                  Logo <span className="text-xs font-normal text-primaryMuted">(Required)</span>
                </Label>
                <CircleInfoIcon fontSize={12} className="text-grey-4" />
              </div>
              <ImagePreview
                imageUrl={settings.logo || 'https://storage.googleapis.com/aspirex-static-files/aspire_light_mark.svg'}
                onRemove={() => { updateSettings({ logo: '' }); }}
                altText="Brand Logo"
                allowChange
                onMediaSelect={(base64Image) => { updateSettings({ logo: base64Image }); }}
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="brandName" className="text-xs font-medium text-foreground">
                Brand Name <span className="text-xs font-normal text-primaryMuted">(Required)</span>
              </Label>
              <div className="px-[1px]">
                <Input id="brandName" placeholder="My Beauty Brand" value={settings.brandName} onChange={(e) => updateSettings({ brandName: e.target.value })} />
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="campaignDates" className="text-xs font-medium text-foreground">Campaign Dates</Label>
              <DatePickerWithRange
                startDate={settings.campaignDates.startDate}
                endDate={settings.campaignDates.endDate}
                buttonClassName="w-full"
                onRangeChange={(dates) => updateSettings({ campaignDates: { startDate: dates.from, endDate: dates.to } })}
              />
            </div>

            <div className="flex flex-col gap-2">
              <div>
                <Label className="text-sm font-medium text-foreground">Social Accounts</Label>
                <p className="mb-0 text-xs font-normal text-primaryMuted">Add quick links to your brand's social accounts.</p>
              </div>
              <div className="flex flex-col gap-2">
                <div className="relative flex items-center px-[1px]">
                  <div className="absolute pointer-events-none left-[1px] h-full w-10 flex items-center justify-center bg-grey-1 border border-border rounded-l-lg">
                    <InstagramIcon fontSize={16} />
                  </div>
                  <Input id="instagram" placeholder="@brandInstagram" className="pl-12" value={settings.socialAccounts.instagram} onChange={(e) => updateSettings({ socialAccounts: { ...settings.socialAccounts, instagram: e.target.value } })} />
                </div>
                <div className="relative flex items-center px-[1px]">
                  <div className="absolute pointer-events-none left-[1px] h-full w-10 flex items-center justify-center bg-grey-1 border border-border rounded-l-lg">
                    <TiktokIcon fontSize={16} />
                  </div>
                  <Input id="tiktok" placeholder="@brandTikTok" className="pl-12" value={settings.socialAccounts.tiktok} onChange={(e) => updateSettings({ socialAccounts: { ...settings.socialAccounts, tiktok: e.target.value } })} />
                </div>
                <div className="relative flex items-center px-[1px]">
                  <div className="absolute pointer-events-none left-[1px] h-full w-10 flex items-center justify-center bg-grey-1 border border-border rounded-l-lg">
                    <YoutubeIcon fontSize={16} />
                  </div>
                  <Input id="youtube" placeholder="youtube.com/brand" className="pl-12" value={settings.socialAccounts.youtube} onChange={(e) => updateSettings({ socialAccounts: { ...settings.socialAccounts, youtube: e.target.value } })} />
                </div>
                <div className="relative flex items-center px-[1px]">
                  <div className="absolute pointer-events-none left-[1px] h-full w-10 flex items-center justify-center bg-grey-1 border border-border rounded-l-lg">
                    <PinterestIcon fontSize={16} />
                  </div>
                  <Input id="pinterest" placeholder="pinterest.com/brandpinterest" className="pl-12" value={settings.socialAccounts.pinterest} onChange={(e) => updateSettings({ socialAccounts: { ...settings.socialAccounts, pinterest: e.target.value } })} />
                </div>
                <div className="relative flex items-center px-[1px]">
                  <div className="absolute pointer-events-none left-[1px] h-full w-10 flex items-center justify-center bg-grey-1 border border-border rounded-l-lg">
                    <GlobeIcon fontSize={16} />
                  </div>
                  <Input id="website" placeholder="brandwebsite.com" className="pl-12" value={settings.socialAccounts.website} onChange={(e) => updateSettings({ socialAccounts: { ...settings.socialAccounts, website: e.target.value } })} />
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="pageUrl" className="text-xs font-medium text-foreground">Page URL</Label>
              <div className="px-[1px] pb-[1px]">
                <Input
                  id="pageUrl"
                  className="flex-1"
                  value={fullUrl}
                  onChange={(e) => {
                    const inputValue = e.target.value;
                    // Extract the pageUrl part from the full URL
                    const urlParts = inputValue.split('/join/');
                    if (urlParts.length > 1) {
                      const afterJoin = urlParts[1];
                      const pageUrlPart = afterJoin.split('?')[0];
                      updateSettings({ pageUrl: pageUrlPart });
                    }
                  }}
                  onFocus={(e) => {
                    const pageUrlStart = fullUrl.indexOf('/join/') + 6;
                    const pageUrlEnd = fullUrl.indexOf('?');
                    if (pageUrlStart !== -1 && pageUrlEnd !== -1) {
                      e.target.setSelectionRange(pageUrlStart, pageUrlEnd);
                    }
                  }}
                />
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        <Separator />

        <AccordionItem value="theme" className="border-b-0">
          <AccordionTrigger>
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-grey-5 rounded-lg text-primary-foreground flex items-center justify-center">
                <PaintbrushIcon />
              </div>
              <span className="text-base font-medium">Theme & Typography</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label className="text-xs font-medium text-primaryMuted">Background Colors</Label>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-1">
                    <Label htmlFor="bgPrimary" className="text-xs font-medium text-foreground">Primary</Label>
                    <CircleInfoIcon fontSize={12} className="text-grey-4" />
                  </div>
                  <div className="flex items-center gap-2">
                    <ColorPicker
                      containerClassName="w-full"
                      popoverWrapperClassName="w-full"
                      value={settings.bgPrimary}
                      onChange={(color) => updateSettings({ bgPrimary: color })}
                    />
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-1">
                    <Label htmlFor="bgSecondary" className="text-xs font-medium text-foreground">Secondary</Label>
                    <CircleInfoIcon fontSize={12} className="text-grey-4" />
                  </div>
                  <div className="flex items-center gap-2">
                    <ColorPicker
                      containerClassName="w-full"
                      popoverWrapperClassName="w-full"
                      value={settings.bgSecondary}
                      onChange={(color) => updateSettings({ bgSecondary: color })}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <Label className="text-xs font-medium text-primaryMuted">Title Colors</Label>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-1">
                    <Label htmlFor="titlePrimary" className="text-xs font-medium text-foreground">Primary</Label>
                    <CircleInfoIcon fontSize={12} className="text-grey-4" />
                  </div>
                  <div className="flex items-center gap-2">
                    <ColorPicker
                      containerClassName="w-full"
                      popoverWrapperClassName="w-full"
                      value={settings.titlePrimary}
                      onChange={(color) => updateSettings({ titlePrimary: color })}
                    />
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-1">
                    <Label htmlFor="titleSecondary" className="text-xs font-medium text-foreground">Secondary</Label>
                    <CircleInfoIcon fontSize={12} className="text-grey-4" />
                  </div>
                  <div className="flex items-center gap-2">
                    <ColorPicker
                      containerClassName="w-full"
                      popoverWrapperClassName="w-full"
                      value={settings.titleSecondary}
                      onChange={(color) => updateSettings({ titleSecondary: color })}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <Label className="text-xs font-medium text-primaryMuted">Body Text Colors</Label>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-1">
                    <Label htmlFor="bodyPrimary" className="text-xs font-medium text-foreground">Primary</Label>
                    <CircleInfoIcon fontSize={12} className="text-grey-4" />
                  </div>
                  <div className="flex items-center gap-2">
                    <ColorPicker
                      containerClassName="w-full"
                      popoverWrapperClassName="w-full"
                      value={settings.bodyPrimary}
                      onChange={(color) => updateSettings({ bodyPrimary: color })}
                    />
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-1">
                    <Label htmlFor="bodySecondary" className="text-xs font-medium text-foreground">Secondary</Label>
                    <CircleInfoIcon fontSize={12} className="text-grey-4" />
                  </div>
                  <div className="flex items-center gap-2">
                    <ColorPicker
                      containerClassName="w-full"
                      popoverWrapperClassName="w-full"
                      value={settings.bodySecondary}
                      onChange={(color) => updateSettings({ bodySecondary: color })}
                    />
                  </div>
                </div>
              </div>
            </div>

            <Separator />

            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-2">
                <Label htmlFor="titleFont" className="text-xs font-medium text-foreground">Title Font</Label>
                <div className="pl-[1px]">
                  <FontPicker
                    value={settings.titleFont.fontFamily}
                    onChange={(font) => updateSettings({ titleFont: { ...settings.titleFont, fontFamily: font } })}
                  />
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="titleFontSize" className="text-xs font-medium text-foreground">Font Size</Label>
                <div className="pr-[1px]">
                  <Select
                    value={String(settings.titleFont.fontSize || 16)}
                    onValueChange={(value) => updateSettings({ titleFont: { ...settings.titleFont, fontSize: parseInt(value, 10) } })}
                  >
                    <SelectTrigger id="titleFontSize"><SelectValue placeholder="Select size" /></SelectTrigger>
                    <SelectContent>
                      {fontSizeOptions.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-2">
                <Label htmlFor="bodyFont" className="text-xs font-medium text-foreground">Body Font</Label>
                <div className="pl-[1px]">
                  <FontPicker
                    value={settings.bodyFont.fontFamily}
                    onChange={(font) => updateSettings({ bodyFont: { ...settings.bodyFont, fontFamily: font } })}
                  />
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="bodyFontSize" className="text-xs font-medium text-foreground">Font Size</Label>
                <div className="pr-[1px]">
                  <Select
                    value={String(settings.bodyFont.fontSize || 14)}
                    onValueChange={(value) => updateSettings({ bodyFont: { ...settings.bodyFont, fontSize: parseInt(value, 10) } })}
                  >
                    <SelectTrigger id="bodyFontSize"><SelectValue placeholder="Select size" /></SelectTrigger>
                    <SelectContent>
                      {fontSizeOptions.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        <Separator />

        <AccordionItem value="buttons" className="border-b-0">
          <AccordionTrigger>
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-green-2 rounded-lg text-primary-foreground flex items-center justify-center">
                <ArrowPointerIcon />
              </div>
              <span className="text-base font-medium">Button Styles</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="flex flex-col gap-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-2">
                <Label htmlFor="buttonFont" className="text-xs font-medium text-foreground">Font</Label>
                <div className="pl-[1px]">
                  <FontPicker
                    value={settings.buttonFont.fontFamily}
                    onChange={(font) => updateSettings({ buttonFont: { ...settings.buttonFont, fontFamily: font } })}
                  />
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="buttonFontSize" className="text-xs font-medium text-foreground">Font Size</Label>
                <div className="pr-[1px]">
                  <Select
                    value={String(settings.buttonFont.fontSize || 14)}
                    onValueChange={(value) => updateSettings({ buttonFont: { ...settings.buttonFont, fontSize: parseInt(value, 10) } })}
                  >
                    <SelectTrigger id="buttonFontSize"><SelectValue placeholder="Select size" /></SelectTrigger>
                    <SelectContent>
                      {fontSizeOptions.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-1">
                  <Label htmlFor="buttonBg" className="text-xs font-medium text-foreground">Background</Label>
                  <CircleInfoIcon fontSize={12} className="text-grey-4" />
                </div>
                <div className="flex items-center gap-2">
                  <ColorPicker
                    containerClassName="w-full"
                    popoverWrapperClassName="w-full"
                    value={settings.buttonBg}
                    onChange={(color) => updateSettings({ buttonBg: color })}
                  />
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-1">
                  <Label htmlFor="buttonTextColor" className="text-xs font-medium text-foreground">Text Color</Label>
                  <CircleInfoIcon fontSize={12} className="text-grey-4" />
                </div>
                <div className="flex items-center gap-2">
                  <ColorPicker
                    containerClassName="w-full"
                    popoverWrapperClassName="w-full"
                    value={settings.buttonTextColor}
                    onChange={(color) => updateSettings({ buttonTextColor: color })}
                  />
                </div>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </>
  );
};
